import {
    asBoolean,
    asMaybeNumber,
    asMaybeString,
    asString,
    loadedDefinedMap,
} from "common/components/component-helpers";
import { ImageAspectRatio } from "common/components/image-types";
import { LOADING, showProComponents } from "common/components/types";
import type { GlideFunctionalRenderer } from "common/components/uis";
import { isOlderThan } from "common/Support";
import { useForce } from "hooks/use-force";
import { useForceValueProducers } from "hooks/use-value-producers";
import { useContext } from "react";
import { Webview } from "../ui/smart/webview/webview";
import { HighlightGrowContext } from "./highlight-component-material-renderer";

const linksTracked = new Map<string, Date>();
export const WebviewMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const [force] = useForce();
    const [[maybeLink, aspectRatio, title, allowScrolling, explicitHeight], [, , , , explicitHeightVP]] =
        useForceValueProducers(getValueProducers, force, ctx);

    const setGrow = useContext(HighlightGrowContext);
    if (aspectRatio === ImageAspectRatio.Expand) {
        setGrow("always");
    } else {
        setGrow("never");
    }

    if (explicitHeightVP !== undefined) {
        // FIXME: This is super ugly.  When we're in a web view component,
        // as opposed to in the web view screen, we get passed an
        // explicit height.  The first one is a Pro component, the second
        // is not, so we use that to differentiate.
        if (!showProComponents(env.appEnvironment)) return null;
    }
    if (maybeLink === LOADING || aspectRatio === LOADING) return null;
    const link = asMaybeString(maybeLink);
    if (link === undefined) return null;

    const dateTracked = linksTracked.get(link);
    // We track the same link at most once per 5 minutes
    if (dateTracked === undefined || isOlderThan(dateTracked, 5 * 60 * 1000)) {
        env.appEnvironment.appFacilities.trackEvent("web_view", { app_id: env.appEnvironment.appID, url: link });

        // Now and then we clear them out and might get a few
        // more than once per 5 minutes.
        if (linksTracked.size > 200) {
            linksTracked.clear();
        }

        linksTracked.set(link, new Date());
    }

    return (
        <Webview
            link={link}
            aspectRatio={aspectRatio as ImageAspectRatio}
            title={loadedDefinedMap(title, asString)}
            allowScrolling={loadedDefinedMap(allowScrolling, asBoolean) ?? false}
            explicitHeight={loadedDefinedMap(explicitHeight, asMaybeNumber)}
        />
    );
};
